import React, { useEffect, useState } from 'react';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import ContactUsMendoza from '../../components/ContactUs/ContactUsMendoza';
import { images } from '../../components/shared/assets';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures, initializeDragScroll } from '../../utils/interactiveFeatures';
import { FilterBar } from '../../components/shared/FilterBar';
import { Button } from '../../components/shared/Button/Button';
import './MeetingsEventsMendoza.css';

const MeetingsEventsMendoza: React.FC = () => {
  const { scrollState } = useScrollManager();
  const { setHoveredDestination } = useOverlayManager();
  const [selectedEventType, setSelectedEventType] = useState<string | undefined>(undefined);

  useEffect(() => {
    window.scrollTo(0, 0);
    initializeAllFeatures();
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);
    
    // FilterBar component handles filter functionality
    // No manual event listeners needed
  }, []);

  return (
    <div className="meetings-events-mendoza">
      {/* Navigation - Mendoza variant (light) */}
      <UnifiedNavigation variant="light" destination="mendoza" />

      {/* Hero Section with Diptych - Full Screen 50/50 */}
      <section className="meetings-hero-diptych" data-scroll-section>
        <div className="meetings-hero-text-block">
          <p className="meetings-hero-description">
            In the heart of Argentina's wine country — where ancient vines twist toward the Andes, 
            sun-drenched vineyards paint the landscape, and centuries of winemaking tradition blend 
            with innovation — Awasi Lodges was born to curate your journey through Mendoza's terroir.
          </p>
        </div>
        <div 
          className="meetings-hero-image-block"
          style={{ backgroundImage: `url('/assets/meetings and gatherings header image.jpg')` }}
        />
      </section>

      {/* Event Spaces Gallery with Filters */}
      <section className="meetings-occasions-section" data-scroll-section>
        <div className="meetings-occasions-content">
          {/* Filter buttons */}
          <FilterBar
            className="meetings-filter-bar"
            dropdowns={[
              {
                id: 'destination',
                type: 'destination',
                level: 'mendoza',
                options: [{ id: 'mendoza', value: 'mendoza', label: 'AWASI MENDOZA' }],
                selectedValue: 'mendoza',
                onSelect: (value: string) => console.log('Destination:', value)
              },
              {
                id: 'type',
                type: 'type',
                level: 'mendoza',
                options: [
                  { id: 'corporate', value: 'corporate', label: 'CORPORATE' },
                  { id: 'celebrations', value: 'celebrations', label: 'CELEBRATIONS' },
                  { id: 'weddings', value: 'weddings', label: 'WEDDINGS' },
                  { id: 'groups', value: 'groups', label: 'GROUPS' },
                  { id: 'incentive', value: 'incentive', label: 'INCENTIVE' }
                ],
                selectedValue: selectedEventType,
                onSelect: (value: string) => setSelectedEventType(value === selectedEventType ? undefined : value)
              }
            ]}
            alignment="left"
          />

          {/* Occasions Gallery - 3x2 Grid */}
          <div className="meetings-occasions-gallery">
            {/* Row 1 */}
            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image">
                <img src="/assets/images/localhost/e148a3c9b1b41cdcfe55bca79640b870fcb427c8.png" alt="Wine Celebrations" />
              </div>
              <div className="meetings-occasion-info">
                <h3>Wine Celebrations</h3>
                <p>Toast special moments in our private wine cave or beneath the stars in the vineyard. Our Mendoza lodge offers intimate celebrations paired with exceptional wines and mountain views.</p>
                <a href="/contact" className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </a>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image">
                <img src="/assets/images/localhost/3c040adef04baeda73725b5b017494444ef32dec.png" alt="Wine Tours" />
              </div>
              <div className="meetings-occasion-info">
                <h3>Private Wine Tours</h3>
                <p>Explore prestigious bodegas with exclusive access to winemakers and rare vintages. Our expert sommeliers craft bespoke tasting journeys through Mendoza's diverse wine regions.</p>
                <a href="/contact" className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </a>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image">
                <img src="/assets/images/localhost/157764183566073919c0adb5caa263ab6baf7a8e.png" alt="Vineyard Weddings" />
              </div>
              <div className="meetings-occasion-info">
                <h3>Vineyard Weddings</h3>
                <p>Exchange vows among ancient vines with the Andes as your witness. Our wine country retreat creates magical moments where Argentine tradition meets luxury hospitality.</p>
                <a href="/contact" className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </a>
              </div>
            </div>

            {/* Row 2 */}
            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image placeholder">
                {/* Placeholder for future content */}
              </div>
              <div className="meetings-occasion-info">
                <h3>Corporate Wine Retreats</h3>
                <p>Inspire your team in the prestigious wine valleys of Mendoza. Our exclusive lodge provides the perfect setting for strategic thinking, team building, and celebrating success with world-class wines.</p>
                <a href="/contact" className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </a>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image placeholder">
                {/* Placeholder for future content */}
              </div>
              <div className="meetings-occasion-info">
                <h3>Harvest Experiences</h3>
                <p>Participate in the vendimia harvest tradition with exclusive access to working vineyards. Blend team building with winemaking, creating lasting bonds over shared vineyard experiences.</p>
                <a href="/contact" className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </a>
              </div>
            </div>

            <div className="meetings-occasion-card">
              <div className="meetings-occasion-image placeholder">
                {/* Placeholder for future content */}
              </div>
              <div className="meetings-occasion-info">
                <h3>Gastronomic Events</h3>
                <p>Create extraordinary culinary moments with wine pairings by renowned chefs. Our Mendoza sanctuary offers unparalleled settings for intimate dinners and grand celebrations.</p>
                <a href="/contact" className="meetings-contact-link">
                  CONTACT US
                  <span className="link-underline"></span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diptych Carousel Section */}
      <section className="meetings-diptych-carousel horizontal-carousel-section" data-scroll-section>
        <div className="meetings-diptych-carousel__container" id="draggable-carousel">
          {/* First Diptych - Text with Small Image + Large Image */}
          <div className="meetings-diptych">
            <div className="meetings-diptych__text-panel">
              <ul className="meetings-diptych__checklist">
                <li>Exclusive access to premier wineries and private cellars</li>
                <li>Helicopter tours over the Andes wine valleys</li>
                <li>Private cooking classes with Argentine master chefs</li>
                <li>Sunset horseback rides through the vineyards</li>
              </ul>
              <div className="meetings-diptych__small-image">
                <img src="/assets/images/localhost/c33b334690d591338bf28c1e9153c04d11f8dba5.png" alt="Small detail" />
              </div>
            </div>
            <div className="meetings-diptych__image-panel">
              <img 
                src="/assets/images/localhost/6200c3abfabed50d490407fe1f8df401c703ad98.png" 
                alt="Mendoza landscape" 
                className="meetings-diptych__image"
              />
            </div>
          </div>

          {/* Scattered Collage Section */}
          <div className="meetings-scattered-collage">
            <div className="meetings-scattered__image meetings-scattered__image--1">
              <img src="/assets/images/localhost/0a1dff6a9e6afa9f3a7b16c78deaffb435413058.png" alt="Interior space" />
            </div>
            <div className="meetings-scattered__image meetings-scattered__image--2">
              <img src="/assets/images/localhost/b4326814b9fb849f82a940e0796e511a7be7446b.png" alt="Meeting room" />
            </div>
            <div className="meetings-scattered__image meetings-scattered__image--3">
              <img src="/assets/images/localhost/4f8b22055f18ee8c9dacf02b5b640a93cfb017f0.png" alt="Lounge area" />
            </div>
          </div>

          {/* Single Full Image */}
          <div className="meetings-single-image">
            <img 
              src="/assets/images/localhost/33d5b125b1c7c803c9ceee3a7ef98e88d3a63f38.png" 
              alt="Pool area" 
              className="meetings-single-image__img"
            />
          </div>

          {/* Triptych Section */}
          <div className="meetings-triptych">
            <div className="meetings-triptych__image">
              <img src="/assets/images/localhost/5226d5c176daa595f4db55d4de23caf126e503b3.png" alt="Terrace view" />
            </div>
            <div className="meetings-triptych__image">
              <img src="/assets/images/localhost/476a389e59073cbfded36ae33e3246a8855d3f77.png" alt="Interior detail" />
            </div>
            <div className="meetings-triptych__image">
              <img src="/assets/images/localhost/81debd7e0047e7bf4ae1a23d7db43d63c1b81226.png" alt="Outdoor space" />
            </div>
          </div>

          {/* Final Full Image */}
          <div className="meetings-final-image">
            <img 
              src="/assets/images/localhost/d8dbaa08f904fa823929d0f428310920f12625c7.png" 
              alt="Awasi Mendoza pool area" 
              className="meetings-final-image__img"
            />
          </div>
        </div>
      </section>

      {/* Contact Us Section with Mendoza Background */}
      <section className="meetings-contact-us-section" data-scroll-section>
        <ContactUsMendoza />
      </section>

      {/* Instagram Gallery */}
      <InstagramSection />

      {/* Footer - Mendoza variant */}
      <Footer variant="mendoza" />
    </div>
  );
};

export default MeetingsEventsMendoza;